import { useQuery } from '@tanstack/react-query'
import { Mail, MessageCircle, Phone } from 'lucide-react'
import { useState } from 'react'

import { ErrorDeLaApi, consulta, pedir } from '../../api/nucleo'
import { Aviso, Boton, Campo, Modal, Pastilla, Selector, clases, claseDeEntrada } from '../../componentes/ui'
import { DevolverDinero } from '../DevolverDinero'
import { dinero, nombreDePasarela } from '../tiendaEnLlano'
import { ESTADOS, ORIGENES, textoDeFecha } from './calendario'
import { type Calendario, type Hueco, numeroDeWhatsApp, type ReservaDelCalendario, valorLegible } from './tipos'

/**
 * La ficha de una reserva: quién, qué, cuándo y con quién; lo que contestó (con las etiquetas de sus
 * preguntas); y lo que hace quien gestiona la agenda: confirmar, «asistió», «no vino», cancelar,
 * cambiar la hora y apuntar notas. Las reglas las pone el núcleo: una cancelada no se reabre, y mover
 * comprueba que la hora nueva está libre. Si se cobró al reservar, se ve el pago y se puede devolver
 * todo o una parte por la misma pasarela (§33), como en un pedido.
 */
export const FichaDeReserva = ({
  espacio,
  sitio,
  datos,
  reserva,
  puedeGestionar,
  cerrar,
  cambiada,
}: {
  espacio: number
  sitio: number
  datos: Calendario
  reserva: ReservaDelCalendario
  puedeGestionar: boolean
  cerrar: () => void
  cambiada: () => void
}) => {
  const [devolviendo, setDevolviendo] = useState(false)
  const pago = reserva.pago
  const quedaPorDevolver = pago && (pago.estado === 'pagado' || pago.estado === 'reembolsado') ? Math.max(0, pago.importe - pago.devuelto) : 0
  const [fallo, setFallo] = useState<null | string>(null)
  const [enviando, setEnviando] = useState<null | string>(null)
  const [notas, setNotas] = useState(reserva.notas ?? '')
  const [moviendo, setMoviendo] = useState(false)
  const servicio = datos.servicios.find((s) => s.id === reserva.servicio?.id)
  const nombreDe = (id: number) => datos.recursos.find((r) => r.id === id)?.nombre ?? 'Otro recurso'
  const estado = ESTADOS[reserva.estado] ?? { texto: reserva.estado, clave: 'neutra' as const }
  const whatsapp = numeroDeWhatsApp(reserva.telefono)
  const cancelada = reserva.estado === 'cancelada'

  const guardar = async (que: string, cuerpo: Record<string, unknown>, preguntar?: string) => {
    if (preguntar && !window.confirm(preguntar)) return
    setEnviando(que)
    setFallo(null)
    try {
      await pedir(`/reservas/${reserva.id}`, { metodo: 'PATCH', cuerpo })
      cambiada()
    } catch (e) {
      setFallo(e instanceof ErrorDeLaApi ? e.message : 'No se ha podido guardar. Vuelve a intentarlo.')
    } finally {
      setEnviando(null)
    }
  }

  return (
    <Modal ancho="max-w-xl" cerrar={cerrar} titulo={`Reserva ${reserva.numero}`}>
      <div className="flex flex-col gap-4 text-sm">
        <div className="flex flex-wrap items-center gap-2">
          <Pastilla clave={estado.clave}>{estado.texto}</Pastilla>
          <span className="text-xs text-tenue">Llegó por {ORIGENES[reserva.origen]?.toLowerCase() ?? reserva.origen}</span>
        </div>
        <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5">
          <dt className="text-tenue">Quién</dt>
          <dd className="font-medium">{reserva.nombre}</dd>
          <dt className="text-tenue">Qué</dt>
          <dd>
            {reserva.servicio?.nombre ?? '—'}
            {reserva.cantidad > 1 ? ` · ${reserva.cantidad} ${reserva.tipo === 'mesa' ? 'comensales' : reserva.tipo === 'estancia' ? 'unidades' : 'plazas'}` : ''}
          </dd>
          <dt className="text-tenue">Cuándo</dt>
          <dd>
            {textoDeFecha(reserva.fecha)}, de {reserva.hora} a {reserva.horaFin}
          </dd>
          <dt className="text-tenue">Con</dt>
          <dd>{reserva.recursos.map(nombreDe).join(' y ')}</dd>
          {pago && (
            <>
              <dt className="text-tenue">Pago</dt>
              <dd className="flex flex-wrap items-center gap-2">
                <span>{textoDelPago(pago)}</span>
                {puedeGestionar && quedaPorDevolver > 0 && pago.pasarela && (
                  <Boton onClick={() => setDevolviendo(true)} pequeno tipo="discreto">
                    Devolver dinero
                  </Boton>
                )}
              </dd>
            </>
          )}
        </dl>
        {devolviendo && pago?.pasarela && (
          <DevolverDinero
            cerrar={() => setDevolviendo(false)}
            cobrado={pago.importe}
            comprador={reserva.email ?? reserva.nombre}
            de="reserva"
            devuelto={pago.devuelto}
            espacio={espacio}
            estado={reserva.estado}
            existenciasDescontadas={false}
            hecho={() => {
              setDevolviendo(false)
              cambiada()
            }}
            moneda={pago.moneda}
            pasarela={pago.pasarela}
            pedido={reserva.id}
            queda={quedaPorDevolver}
          />
        )}

        {(reserva.telefono || reserva.email) && (
          <div className="flex flex-wrap gap-2">
            {reserva.telefono && (
              <a className="inline-flex h-8 items-center gap-1.5 rounded-boton border border-linea px-3 text-[13px]" href={`tel:${reserva.telefono}`}>
                <Phone className="size-3.5" /> {reserva.telefono}
              </a>
            )}
            {whatsapp && (
              <a className="inline-flex h-8 items-center gap-1.5 rounded-boton border border-linea px-3 text-[13px]" href={`https://wa.me/${whatsapp}`} rel="noreferrer" target="_blank">
                <MessageCircle className="size-3.5" /> WhatsApp
              </a>
            )}
            {reserva.email && (
              <a className="inline-flex h-8 items-center gap-1.5 rounded-boton border border-linea px-3 text-[13px]" href={`mailto:${reserva.email}`}>
                <Mail className="size-3.5" /> {reserva.email}
              </a>
            )}
          </div>
        )}

        {reserva.datos && Object.keys(reserva.datos).length > 0 && (
          <div>
            <h3 className="mb-1.5 text-[13px] font-semibold">Lo que contestó</h3>
            <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1">
              {Object.entries(reserva.datos).map(([clave, valor]) => {
                const campo = servicio?.campos.find((c) => c.clave === clave)
                return (
                  <div className="contents" key={clave}>
                    <dt className="text-tenue">{campo?.etiqueta ?? clave}</dt>
                    <dd className="break-words">{valorLegible(campo, valor)}</dd>
                  </div>
                )
              })}
            </dl>
          </div>
        )}

        {puedeGestionar && !cancelada && (
          <div className="flex flex-wrap gap-2">
            {reserva.estado === 'pendiente' && (
              <Boton cargando={enviando === 'confirmada'} onClick={() => guardar('confirmada', { estado: 'confirmada' })} pequeno tipo="primario">
                Confirmar
              </Boton>
            )}
            {reserva.estado !== 'asistio' && (
              <Boton cargando={enviando === 'asistio'} onClick={() => guardar('asistio', { estado: 'asistio' })} pequeno>
                Asistió
              </Boton>
            )}
            {reserva.estado !== 'no-vino' && (
              <Boton cargando={enviando === 'no-vino'} onClick={() => guardar('no-vino', { estado: 'no-vino' })} pequeno>
                No vino
              </Boton>
            )}
            {reserva.tipo !== 'estancia' && (
              <Boton onClick={() => setMoviendo((m) => !m)} pequeno tipo="discreto">
                Cambiar la hora
              </Boton>
            )}
            <Boton cargando={enviando === 'cancelada'} onClick={() => guardar('cancelada', { estado: 'cancelada' }, '¿Cancelar esta reserva? Su hueco queda libre y no se puede reabrir: si cambia de idea, se hace otra.')} pequeno tipo="peligro">
              Cancelar la reserva
            </Boton>
          </div>
        )}
        {cancelada && <Aviso tono="info">Está cancelada: su hueco quedó libre. Si la persona vuelve a querer venir, haz una reserva nueva.</Aviso>}

        {moviendo && servicio && <CambiarHora cambiada={cambiada} datos={datos} reserva={reserva} servicioRecursos={servicio.recursos} sitio={sitio} />}

        {puedeGestionar && (
          <div className="flex flex-col gap-1.5">
            <label className="text-[13px] font-semibold" htmlFor="notas-de-la-reserva">
              Notas internas
            </label>
            <textarea className={clases(claseDeEntrada(), 'min-h-16 py-2')} id="notas-de-la-reserva" maxLength={2000} onChange={(e) => setNotas(e.target.value)} value={notas} />
            {notas !== (reserva.notas ?? '') && (
              <div>
                <Boton cargando={enviando === 'notas'} onClick={() => guardar('notas', { notas })} pequeno>
                  Guardar las notas
                </Boton>
              </div>
            )}
          </div>
        )}
        {fallo && <Aviso tono="error">{fallo}</Aviso>}
      </div>
    </Modal>
  )
}

const CambiarHora = ({ sitio, datos, reserva, servicioRecursos, cambiada }: { sitio: number; datos: Calendario; reserva: ReservaDelCalendario; servicioRecursos: number[]; cambiada: () => void }) => {
  const [dia, setDia] = useState(reserva.fecha)
  const [recurso, setRecurso] = useState<number>(reserva.recursos[0] ?? servicioRecursos[0] ?? 0)
  const [fallo, setFallo] = useState<null | string>(null)
  const [enviando, setEnviando] = useState<null | string>(null)
  const huecos = useQuery({
    queryKey: ['agenda-huecos-mover', sitio, reserva.id, dia, recurso],
    queryFn: () => pedir<{ huecos: Hueco[] }>(`/agenda/huecos${consulta({ sitio, servicio: reserva.servicio!.id, fecha: dia, recurso, cantidad: reserva.cantidad, excluir: reserva.id })}`),
    enabled: Boolean(reserva.servicio),
  })
  const mover = async (desde: string) => {
    setEnviando(desde)
    setFallo(null)
    try {
      await pedir('/agenda/reservas/mover', { metodo: 'POST', cuerpo: { sitio, reserva: reserva.id, desde, recurso } })
      cambiada()
    } catch (e) {
      setFallo(e instanceof ErrorDeLaApi ? e.message : 'No se ha podido cambiar la hora.')
    } finally {
      setEnviando(null)
    }
  }
  return (
    <div className="flex flex-col gap-3 rounded-caja border border-linea bg-papel-2 p-3">
      <div className="grid gap-3 sm:grid-cols-2">
        <Campo etiqueta="Día" onChange={(e) => setDia(e.target.value)} type="date" value={dia} />
        {servicioRecursos.length > 1 && (
          <label className="flex flex-col gap-1.5">
            <span className="text-[13px] font-medium">Con</span>
            <Selector onChange={(e) => setRecurso(Number(e.target.value))} value={recurso}>
              {servicioRecursos.map((r) => (
                <option key={r} value={r}>
                  {datos.recursos.find((x) => x.id === r)?.nombre ?? `Recurso ${r}`}
                </option>
              ))}
            </Selector>
          </label>
        )}
      </div>
      {huecos.isPending ? (
        <p className="text-tenue">Buscando huecos…</p>
      ) : (huecos.data?.huecos ?? []).length === 0 ? (
        <p className="text-tenue">No queda ningún hueco ese día.</p>
      ) : (
        <div className="flex flex-wrap gap-1.5">
          {huecos.data!.huecos.map((h) => (
            <Boton cargando={enviando === h.desde} key={h.desde} onClick={() => mover(h.desde)} pequeno>
              {h.hora}
            </Boton>
          ))}
        </div>
      )}
      {fallo && <Aviso tono="error">{fallo}</Aviso>}
    </div>
  )
}

/** El pago de una reserva en una línea: «S/ 10.00 pagado con Culqi», y lo devuelto si lo hay. */
const textoDelPago = (pago: NonNullable<ReservaDelCalendario['pago']>): string => {
  const con = pago.pasarela ? ` con ${nombreDePasarela(pago.pasarela)}` : ''
  if (pago.estado === 'pagado' || pago.estado === 'reembolsado') {
    if (pago.devuelto >= pago.importe) return `${dinero(pago.importe, pago.moneda)} pagado${con} y devuelto`
    return `${dinero(pago.importe, pago.moneda)} pagado${con}${pago.devuelto > 0 ? ` · devuelto ${dinero(pago.devuelto, pago.moneda)}` : ''}`
  }
  if (pago.estado === 'pendiente') return `${dinero(pago.importe, pago.moneda)} sin pagar todavía`
  if (pago.estado === 'fallido') return `El pago de ${dinero(pago.importe, pago.moneda)} no salió`
  return `${dinero(pago.importe, pago.moneda)} · ${pago.estado}`
}
